import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { JOB_STATUSES, JOB_TYPES } from "@koc/shared";
import { apiJobs } from "../api.js";
import { Badge, Button, Input, Label, PageHeader, Table, fmtTime } from "../components/ui.js";

const STATUS_TONE: Record<string, "green" | "red" | "yellow" | "blue" | "neutral"> = {
  pending: "yellow",
  dispatched: "blue",
  running: "blue",
  succeeded: "green",
  failed: "red",
  cancelled: "neutral",
};

export function JobsPage() {
  const [filters, setFilters] = useState<{ status?: string; jobType?: string }>({});
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["jobs", filters], queryFn: () => apiJobs.list(filters) });

  const retry = useMutation({
    mutationFn: (id: number) => apiJobs.retry(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["jobs"] }),
  });

  return (
    <>
      <PageHeader title="Jobs" />
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <Label>Status</Label>
          <select
            className="block w-full rounded border border-stone-300 px-3 py-1.5 text-sm bg-white"
            value={filters.status ?? ""}
            onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
          >
            <option value="">All</option>
            {JOB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label>Type</Label>
          <select
            className="block w-full rounded border border-stone-300 px-3 py-1.5 text-sm bg-white"
            value={filters.jobType ?? ""}
            onChange={(e) => setFilters({ ...filters, jobType: e.target.value || undefined })}
          >
            <option value="">All</option>
            {JOB_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <Button variant="secondary" onClick={() => setFilters({})}>Clear filters</Button>
        </div>
      </div>
      <Table
        rows={q.data?.items ?? []}
        columns={[
          { header: "ID", cell: (r) => r.id, className: "tabular-nums w-16" },
          { header: "Type", cell: (r) => <Badge>{r.job_type}</Badge>, className: "w-32" },
          { header: "Status", cell: (r) => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>, className: "w-28" },
          { header: "Project", cell: (r) => r.project_name ?? "—" },
          { header: "Platform", cell: (r) => r.platform ?? "—", className: "w-24" },
          { header: "Due at", cell: (r) => <span className="text-xs text-stone-500">{fmtTime(r.due_at)}</span> },
          { header: "Attempts", cell: (r) => r.attempts, className: "text-right tabular-nums w-20" },
          { header: "Last error", cell: (r) => r.last_error ? (
            <span title={r.last_error} className="text-xs text-red-600 truncate block max-w-xs">{r.last_error}</span>
          ) : null },
          { header: "", cell: (r) => (
            (r.status === "failed" || r.status === "cancelled" || r.status === "succeeded") ? (
              <Button variant="ghost" onClick={(e) => { e.stopPropagation(); retry.mutate(r.id); }}>
                Retry
              </Button>
            ) : null
          ), className: "w-20" },
        ]}
      />
    </>
  );
}
